// ReportTable.jsx
import React, { useEffect, useRef, useState } from "react";
import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TablePagination,
  Dialog,
  Typography,
  Box,
  Button,
  TextField,
  Alert,
  Chip,
} from "@mui/material";
import { useReportsStore } from "./store/reportsStore";
import ReportDeleteDialog from "./ReportDeleteDialog";
import ReportCancelDialog from "./ReportCancelDialog";
import ReportTableBody from "./ReportTableBody";
import { useParams } from "react-router-dom";
import LicenseAgreementDialog from "./LicenseAgreementDialog";
import QuickUserGuideDialog from "./QuickUserGuideDialog";
import usePaginatedList from "./hooks/usePaginatedList";
import { useProjectTags } from "./projects/ProjectTagField";
import MoveToProjectDialog from "./projects/MoveToProjectDialog";
import { useAuthStore } from "./store/authStore";

const headerCellSx = {
  color: "#64748B",
  fontWeight: 700,
  fontSize: "0.65rem",
  textTransform: "uppercase",
  letterSpacing: 1.2,
  borderBottom: "2px solid #E2E8F0",
  backgroundColor: "#F8FAFC",
  py: 1.5,
};

// `project` (a project ID, or "none" for the Metalytics catch-all) lists every
// user's runs in it instead of one user's — staff only, from a project page.
export default function ReportTable({ userId: propUserId = null, project = null }) {
  const { userId: paramUserId, id: paramId, reportId: paramReportId } = useParams();
  const effectiveUserId = paramUserId || paramId || propUserId;
  // Deep links carrying a reportId (e.g. /report-list/:userId/:reportId,
  // used by ServerStatus, Runs, ReportTableBody's own "view report" links,
  // and Evaluations) show just that one record instead of the owner's
  // whole (potentially huge) report history — being on the 2-segment route
  // IS the request for single-record mode, no extra query param needed.
  const singleRecordOnly = Boolean(paramReportId);

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deleteFileId, setDeleteFileId] = useState(null);
  const [zoomedImage, setZoomedImage] = useState(null);
  const [deletingReportId, setDeletingReportId] = useState(null);
  const [licenseDialogOpen, setLicenseDialogOpen] = useState(false);
  const [userGuideDialogOpen, setUserGuideDialogOpen] = useState(false);
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  const [cancelReportId, setCancelReportId] = useState(null);
  const [cancellingReportId, setCancellingReportId] = useState(null);
  const [actionError, setActionError] = useState(null);

  const { deleteReport, cancelReport } = useReportsStore();

  // The customer's own project tags filter their reports (docs/project-tracking.md).
  // Only on their own list: a reviewer looking at someone else's has no tags.
  const me = useAuthStore((s) => s.user);
  const ownList = !effectiveUserId || effectiveUserId === me?.id;
  const projectTags = useProjectTags();
  const [projectTag, setProjectTag] = useState(null);
  const showTags = !project && ownList && !singleRecordOnly && projectTags.available && projectTags.tags.length > 0;
  // Staff move any run; customers their own, once their company has projects.
  const canMove = projectTags.staff || (ownList && projectTags.available);
  const [moving, setMoving] = useState(null);
  const refreshSignal = useReportsStore((s) => s.refreshSignal);

  // Server-side pagination. The report-list route is paginated,
  // so report_service.get_page_report pushes ORDER BY / LIMIT / OFFSET /
  // COUNT down into Postgres, sourced from `run` (the reliably-synced
  // table) rather than the stale on-disk-only `report` — see the DB-backed
  // report-list gap analysis. Polling refreshes the current page in place.
  const {
    records: reports,
    total,
    page,
    setPage,
    pageSize: rowsPerPage,
    setPageSize: setRowsPerPage,
    q: filter,
    setQ: setFilter,
    refresh,
  } = usePaginatedList("/report-list", {
    initialPageSize: 25,
    // "activity" (most-recent of end/start/queued) instead of plain
    // start_datetime — a queued run has no start_datetime yet, so sorting
    // by that alone pushed every queued run to the very end of the user's
    // whole history (NULLS LAST) instead of near the top where a just-
    // submitted job belongs. Same convention Runs.jsx already uses.
    initialSort: "activity",
    initialOrder: "desc",
    pollMs: 10000,
    extraParams: {
      ...(project ? { project } : { user_id: effectiveUserId }),
      ...(singleRecordOnly ? { report_id: paramReportId } : {}),
      ...(showTags && projectTag ? { project_tag: projectTag } : {}),
    },
  });

  // Skip the initial mount (refreshSignal starts at 0 and usePaginatedList
  // already fetches on mount) — only refetch on signals fired after a run
  // is submitted elsewhere on the page.
  const isFirstRefreshSignal = useRef(true);
  useEffect(() => {
    if (isFirstRefreshSignal.current) {
      isFirstRefreshSignal.current = false;
      return;
    }
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only react to the signal, not to `refresh` identity
  }, [refreshSignal]);

  const handleOpenDeleteDialog = (fileId) => {
    setDeleteFileId(fileId);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteFile = async () => {
    setDeletingReportId(deleteFileId);
    setActionError(null);
    try {
      await deleteReport(deleteFileId, effectiveUserId);
      await refresh();
    } catch (error) {
      console.error("Error deleting file:", error);
      setActionError(`Delete failed: ${error.message || error}`);
    } finally {
      setDeletingReportId(null);
      setIsDeleteDialogOpen(false);
    }
  };

  const handleOpenCancelDialog = (reportId) => {
    setCancelReportId(reportId);
    setIsCancelDialogOpen(true);
  };

  const handleCancelRun = async () => {
    setCancellingReportId(cancelReportId);
    setActionError(null);
    try {
      await cancelReport(cancelReportId, effectiveUserId);
      await refresh();
    } catch (error) {
      console.error("Error cancelling run:", error);
      // Most common cause: report.json is missing for this run (e.g. it
      // never actually got created on disk) — cancel has nothing to update,
      // so surface that instead of leaving the dialog looking like a no-op.
      setActionError(`Cancel failed: ${error.message || error}`);
    } finally {
      setCancellingReportId(null);
      setIsCancelDialogOpen(false);
    }
  };

  // A project page's "N runs" link lands on #runs: scroll there once loaded.
  const scrolledToRuns = useRef(false);
  useEffect(() => {
    if (project && !scrolledToRuns.current && reports.length > 0 && window.location.hash === "#runs") {
      scrolledToRuns.current = true;
      document.getElementById("runs")?.scrollIntoView({ behavior: "smooth" });
    }
  }, [project, reports.length]);

  return (
    <>
      {/* Section header */}
      <Box
        id="runs"
        sx={{
          display: "flex",
          alignItems: "baseline",
          gap: 1,
          px: 1,
          pt: 3,
          pb: 1.5,
          borderTop: "1px solid #E2E8F0",
          mt: 3,
        }}
      >
        <Typography
          component="span"
          sx={{
            fontWeight: 800,
            fontSize: "0.85rem",
            color: "#2F3F5C",
            textTransform: "uppercase",
            letterSpacing: 1.5,
          }}
        >
          Job History
        </Typography>
        {total > 0 && (
          <Typography
            component="span"
            sx={{
              fontSize: "0.72rem",
              color: "#94A3B8",
              fontWeight: 500,
            }}
          >
            {total} {total === 1 ? "run" : "runs"}
          </Typography>
        )}
        {!singleRecordOnly && (
          <TextField
            size="small"
            placeholder="Filter…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            sx={{ ml: 2, minWidth: 220 }}
          />
        )}
        {/* User Guide + License Agreement Buttons */}
        <Box sx={{ flexGrow: 1 }} />
        <Button
          variant="outlined"
          color="primary"
          size="small"
          sx={{ ml: 2, fontWeight: 600 }}
          onClick={() => setUserGuideDialogOpen(true)}
        >
          User Guide
        </Button>
        <Button
          variant="outlined"
          color="primary"
          size="small"
          sx={{ ml: 1, fontWeight: 600 }}
          onClick={() => setLicenseDialogOpen(true)}
        >
          License Agreement
        </Button>
      </Box>

      {actionError && (
        <Alert severity="error" sx={{ mx: 1, mb: 1 }} onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      )}

      {showTags && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, px: 1, pb: 1.5 }} aria-label="Your reports by project">
          <Chip
            label="All"
            color={projectTag ? "default" : "primary"}
            variant={projectTag ? "outlined" : "filled"}
            onClick={() => { setProjectTag(null); setPage(0); }}
          />
          {projectTags.tags.map((t) => (
            <Chip
              key={t.name}
              label={`#${t.name} · ${t.status}`}
              color={projectTag === t.name ? "primary" : "default"}
              variant={projectTag === t.name ? "filled" : "outlined"}
              onClick={() => { setProjectTag(t.name); setPage(0); }}
            />
          ))}
        </Box>
      )}

      <Table>
        <TableHead>
          <TableRow>
            <TableCell sx={{ ...headerCellSx, width: 44 }} />
            <TableCell sx={headerCellSx}>Analysis</TableCell>
            <TableCell sx={headerCellSx}>Status</TableCell>
            <TableCell sx={headerCellSx}>Input Files</TableCell>
            <TableCell sx={headerCellSx}>Started</TableCell>
            <TableCell sx={headerCellSx}>Actions</TableCell>
          </TableRow>
        </TableHead>
        <ReportTableBody
          userId={project ? null : effectiveUserId}
          staff={projectTags.staff}
          onMoveProject={canMove ? (record) => setMoving({
            type: "run", id: record.id, label: `Run ${record.id}`,
            current: projectTags.staff ? record.project_id : record.project_tag,
          }) : null}
          reports={reports}
          onOpenDeleteDialog={handleOpenDeleteDialog}
          onOpenCancelDialog={handleOpenCancelDialog}
          setZoomedImage={setZoomedImage}
          deletingReportId={deletingReportId}
          cancellingReportId={cancellingReportId}
          refresh={refresh}
        />
      </Table>

      {!singleRecordOnly && (
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, next) => setPage(next)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => setRowsPerPage(parseInt(e.target.value, 10))}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      )}

      <Dialog open={!!zoomedImage} onClose={() => setZoomedImage(null)} maxWidth="lg">
        {zoomedImage && (
          <img src={zoomedImage} alt="Zoomed" style={{ width: "100%", height: "auto" }} />
        )}
      </Dialog>

      <MoveToProjectDialog item={moving} projectTags={projectTags} onClose={() => setMoving(null)} onMoved={refresh} />

      <ReportDeleteDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onDelete={handleDeleteFile}
      />

      <ReportCancelDialog
        isOpen={isCancelDialogOpen}
        onClose={() => setIsCancelDialogOpen(false)}
        onCancel={handleCancelRun}
      />

      {/* License Agreement Dialog */}
      <LicenseAgreementDialog open={licenseDialogOpen} onClose={() => setLicenseDialogOpen(false)} />

      {/* User Guide Dialog — manual reopen; the "don't show again" checkbox
          only affects the automatic first-login popup (QuickUserGuideAutoShow),
          so this always opens regardless of that preference. */}
      <QuickUserGuideDialog open={userGuideDialogOpen} onClose={() => setUserGuideDialogOpen(false)} />
    </>
  );
}
